/**
 * CapturePanel — ADR-161 capture, as something a human operates.
 *
 * The mechanism was finished three times before anyone could use it: the compose
 * passthrough was missing, then the deploy-hop export, then the caller. Each time the
 * layer below was done and the layer above assumed. The last version of that mistake was
 * proposing that the operator arm capture from the browser console — which is not an
 * interface, it is a workaround standing in for one.
 *
 * So this is the whole loop in one place: arm it, see that it is armed, run the workflow,
 * see what was recorded, and read a recording. Nothing here needs a shell, a console, or
 * a redeploy.
 *
 * WHAT IT DELIBERATELY SAYS OUT LOUD. Capture arms for exactly ONE run and the run
 * consumes it. That is counter-intuitive for a toggle, and an operator who assumes it
 * stays on will arm once, run twice, and wonder why the second run recorded nothing. The
 * panel states it where the button is, not in a tooltip.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'

import { api, type CaptureIndex } from '../api/client'

const PAGE = 10

/** yyyy-mm-dd hh:mm, local. The absolute time matters as much as "20m ago" once a
 *  recording is something you come back to tomorrow to find. */
function stamp(iso: string | null): string {
  if (!iso) return '—'
  const d = new Date(iso)
  return `${d.toLocaleDateString()} ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
}

export function CapturePanel() {
  const [index, setIndex] = useState<CaptureIndex | null>(null)
  // Collapsed by DEFAULT once there is history: this lives on Diagnostics above the
  // delegation table, and a growing list of runs should not push the page's main content
  // off screen. Expanded when there is nothing yet, so the feature is discoverable.
  const [open, setOpen] = useState(true)
  const [filter, setFilter] = useState('')
  const [page, setPage] = useState(0)
  const [confirming, setConfirming] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [openRun, setOpenRun] = useState<string | null>(null)
  const [files, setFiles] = useState<{ file: string; bytes: number | null }[] | null>(null)
  const [body, setBody] = useState<string | null>(null)

  const refresh = useCallback(async () => {
    try {
      setIndex(await api.captureIndex())
      setError(null)
    } catch (e) {
      // Named, because "the fleet did not answer" and "capture is off" look identical in
      // an empty panel and only one of them is worth acting on.
      setError(e instanceof Error ? e.message : 'could not read capture state')
    }
  }, [])

  useEffect(() => { void refresh() }, [refresh])

  // Collapse once there is history to collapse — but only on first load, so a deliberate
  // expand is never undone by a refresh.
  const [autoCollapsed, setAutoCollapsed] = useState(false)
  useEffect(() => {
    if (!autoCollapsed && index && index.runs.length > 3) {
      setOpen(false)
      setAutoCollapsed(true)
    }
  }, [index, autoCollapsed])

  const runs = useMemo(() => {
    const q = filter.trim().toLowerCase()
    if (!q) return index?.runs ?? []
    // Matches the id, the subjects, and the date — the three things an operator has when
    // looking for "the Clarity one from Tuesday".
    return (index?.runs ?? []).filter(r =>
      r.run_id.toLowerCase().includes(q)
      || (r.subjects ?? []).some(s => s.toLowerCase().includes(q))
      || stamp(r.recorded_at).toLowerCase().includes(q))
  }, [index, filter])

  const pages = Math.max(1, Math.ceil(runs.length / PAGE))
  const shown = runs.slice(page * PAGE, page * PAGE + PAGE)

  async function remove(runId: string) {
    try {
      await api.deleteCaptureRun(runId)
      if (openRun === runId) { setOpenRun(null); setFiles(null); setBody(null) }
      setConfirming(null)
      await refresh()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'could not delete that run')
    }
  }

  async function toggle() {
    if (!index) return
    setBusy(true)
    try {
      if (index.armed) await api.disarmCapture()
      else await api.armCapture()
      await refresh()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'could not change capture state')
    } finally {
      setBusy(false)
    }
  }

  async function openRunFiles(runId: string) {
    setOpenRun(runId)
    setBody(null)
    setFiles(null)
    try {
      setFiles((await api.captureFiles(runId)).files)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'could not list recordings')
    }
  }

  async function openFile(runId: string, file: string) {
    try {
      setBody(JSON.stringify(await api.captureFile(runId, file), null, 2))
    } catch (e) {
      setError(e instanceof Error ? e.message : 'could not read that recording')
    }
  }

  return (
    <section data-testid="capture-panel" className="space-y-4">
      <header className="space-y-1">
        <h2 className="text-lg font-semibold">Record a run</h2>
        <p className="text-sm text-slate-600">
          Records what this fleet&apos;s vendors actually returned, so a step can be replayed
          offline instead of waiting for tomorrow&apos;s run. Payloads are redacted before they
          are written.
        </p>
      </header>

      {error && (
        <p role="alert" className="rounded border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800">
          {error}
        </p>
      )}

      <div className="flex items-center gap-3">
        <button
          type="button"
          disabled={busy || !index}
          onClick={() => void toggle()}
          data-testid="capture-arm-toggle"
          className="rounded bg-slate-800 px-3 py-1.5 text-sm text-white disabled:opacity-50"
        >
          {index?.armed ? 'Cancel recording' : 'Record the next run'}
        </button>
        <span data-testid="capture-armed-state" className="text-sm">
          {index === null
            ? 'checking…'
            : index.armed
              ? 'Armed — the next activity run on this fleet will be recorded.'
              : 'Not recording.'}
        </span>
      </div>

      {index?.armed && (
        // The counter-intuitive bit, stated where the decision is made.
        <p className="text-xs text-slate-500">
          One run only. The next run consumes the arming, so there is nothing to switch off
          afterwards.
        </p>
      )}

      <div className="space-y-2">
        <div className="flex items-center justify-between gap-3">
          <button
            type="button"
            onClick={() => setOpen(o => !o)}
            data-testid="capture-runs-toggle"
            className="text-sm font-semibold hover:underline"
          >
            {open ? '▾' : '▸'} Recorded runs
            {index && <span className="ml-2 font-normal text-slate-500">({runs.length})</span>}
          </button>
          {open && (index?.runs.length ?? 0) > 0 && (
            <input
              value={filter}
              onChange={e => { setFilter(e.target.value); setPage(0) }}
              placeholder="filter by id, tool or date"
              data-testid="capture-filter"
              className="w-56 rounded border px-2 py-1 text-xs"
            />
          )}
        </div>

        {open && index && runs.length === 0 && (
          <p data-testid="capture-empty" className="text-sm text-slate-500">
            {filter
              ? `No recorded run matches “${filter}”.`
              : 'Nothing recorded yet. Arm a recording, then run a workflow.'}
          </p>
        )}

        {open && (
          <ul className="divide-y rounded border">
            {shown.map(r => (
              <li key={r.run_id} className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
                <div className="min-w-0">
                  <button
                    type="button"
                    onClick={() => void openRunFiles(r.run_id)}
                    className="block truncate text-left font-mono text-xs text-blue-700 hover:underline"
                  >
                    {r.run_id}
                  </button>
                  {/* What the recording was FOR. A UUID identifies a run and describes
                      nothing; this is derived from the recordings themselves so it cannot
                      drift from the contents. */}
                  {!!r.subjects?.length && (
                    <span data-testid="capture-subjects" className="block truncate text-xs text-slate-500">
                      {r.subjects.join(', ')}
                    </span>
                  )}
                </div>
                <div className="flex shrink-0 items-center gap-3 text-slate-500">
                  <span>
                    {r.captures} recording{r.captures === 1 ? '' : 's'}
                    {!!r.gaps && (
                      <span data-testid="capture-gap-count" className="ml-2 font-medium text-amber-700">
                        · {r.gaps} not recorded
                      </span>
                    )}
                  </span>
                  <span title={r.recorded_at ?? ''} className="tabular-nums">
                    {stamp(r.recorded_at)}
                  </span>
                  {confirming === r.run_id ? (
                    <span className="flex items-center gap-2">
                      <button type="button" onClick={() => void remove(r.run_id)}
                        data-testid="capture-delete-confirm"
                        className="rounded bg-red-700 px-2 py-0.5 text-xs text-white">Delete</button>
                      <button type="button" onClick={() => setConfirming(null)}
                        className="text-xs hover:underline">cancel</button>
                    </span>
                  ) : (
                    // Confirm first: recordings are the evidence behind an investigation,
                    // and a one-click delete beside a row you clicked to READ is the wrong
                    // affordance.
                    <button type="button" onClick={() => setConfirming(r.run_id)}
                      data-testid="capture-delete"
                      className="text-xs text-slate-400 hover:text-red-700">delete</button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}

        {open && pages > 1 && (
          <div className="flex items-center gap-3 text-xs text-slate-600">
            <button type="button" disabled={page === 0} onClick={() => setPage(p => p - 1)}
              data-testid="capture-prev" className="rounded border px-2 py-0.5 disabled:opacity-40">Previous</button>
            <span data-testid="capture-page">Page {page + 1} of {pages}</span>
            <button type="button" disabled={page >= pages - 1} onClick={() => setPage(p => p + 1)}
              data-testid="capture-next" className="rounded border px-2 py-0.5 disabled:opacity-40">Next</button>
          </div>
        )}
      </div>

      {openRun && (
        <div className="space-y-2">
          <h3 className="text-sm font-semibold">
            <span className="font-mono text-xs">{openRun}</span>
          </h3>
          <ul className="flex flex-wrap gap-2">
            {(files ?? []).map(f => (
              <li key={f.file}>
                <button
                  type="button"
                  onClick={() => void openFile(openRun, f.file)}
                  className="rounded border px-2 py-1 font-mono text-xs hover:bg-slate-50"
                >
                  {f.file.startsWith('_gap.') ? (
                    <span className="text-amber-700">not recorded — open for the reason</span>
                  ) : f.file}
                  {f.bytes !== null && <span className="ml-2 text-slate-400">{f.bytes}B</span>}
                </button>
              </li>
            ))}
          </ul>
          {files?.length === 0 && (
            <p className="text-sm text-slate-500">That run has no recordings.</p>
          )}
          {body && (
            <pre
              data-testid="capture-body"
              className="max-h-96 overflow-auto rounded bg-slate-900 p-3 text-xs text-white"
            >
              {body}
            </pre>
          )}
        </div>
      )}
    </section>
  )
}
